import type { ALL_BLOCKS_DATA, ALL_BLOCKS_PARAMS, CONTENT_STRUCTURE_ROOT, CONTENT_STRUCTURE_SLOT, CONTENT_STRUCTURE_WITH_DATA, CONTENTS, DEVICE_TYPES, UUID } from '~~/shared-components/types/Blocks'
import type { CONTAINER_DATA, CONTAINER_PARAMS } from '~~/shared-components/content/container/types'
import type { PARENT_COL_GAP } from '~~/shared-components/types/Blocks'
import { DEFAULT_CONTAINER_DATA, DEFAULT_CONTAINER_PARAMS } from '../container/const'
import { Blocks } from '~~/shared-components/content/blocks'
import { devices, devicesArray } from '../devices'
import { set, get } from 'lodash-es'
import { Content } from '~~/shared-components/content/settings'
import { useContent } from '..'

export const useContentUtils = () => {
  return { getColsGap }
}

export const getColsGap = (
  childs: ALL_BLOCKS_PARAMS[],
  index: number,
  parentStyles: ALL_BLOCKS_PARAMS
): PARENT_COL_GAP => {
  const deviceRes: { [deviceType in DEVICE_TYPES]?: number } = {}
  Object.values(devices).forEach((d) => {
    const rowItemCounts: number[] = []
    let currentRowWidth = 0
    let currentRowIndex = -1
    let targetRowIndex = 0

    childs?.forEach((child, childIndex) => {
      const currentWidth = child?.[d.value]?.width?.value ?? 100

      if (currentRowIndex === -1 || currentRowWidth + currentWidth > 100) {
        currentRowIndex += 1
        currentRowWidth = currentWidth
        rowItemCounts[currentRowIndex] = 1
      } else {
        currentRowWidth += currentWidth
        rowItemCounts[currentRowIndex] = (rowItemCounts[currentRowIndex] ?? 0) + 1
      }

      if (childIndex === index) {
        targetRowIndex = currentRowIndex
      }
    })

    deviceRes[d.value] = rowItemCounts[targetRowIndex] ?? 1
  })

  return Object.fromEntries(
    devicesArray.map((d) => {
      const { value = 0, unit = 'px' } = parentStyles?.[d.value]?.gap?.cols ?? {}
      return [
        d.value as DEVICE_TYPES,
        deviceRes[d.value]! > 1
          ? { value: (value * Math.max(deviceRes[d.value]! - 1, 1)) / deviceRes[d.value]!, unit }
          : { value: 0, unit: 'px' },
      ]
    })
  )
}

export const deleteBlockByKey = (
  slots: {
    [key: string]: CONTENT_STRUCTURE_SLOT
  },
  keyToDelete: string
) => {
  const nuxtApp = useNuxtApp()
  return nuxtApp.runWithContext(() => {
    const { allContents, selectedContent } = useContent()
    delete allContents.value[selectedContent.value!.uuid]?.content.structureContent?.[keyToDelete]
    const deleteBlock = (root: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT) => {
      if (!root || !root.children) return false

      for (let i = 0; i < root.children!.length; i++) {
        // Pokud klíč odpovídá, smažte aktuální prvek pomocí splice
        if (root.children![i]!.key === keyToDelete) {
          root.children!.splice(i, 1)
          return true // Objekt byl nalezen a odstraněn
        }
        // Rekurzivně prohledávejte podřízené prvky
        if (root.children![i]!.children && Array.isArray(root.children![i]!.children)) {
          const foundAndRemoved = deleteBlock(root.children![i]!)
          if (foundAndRemoved) {
            return true // Objekt byl nalezen a odstraněn v podřízených prvcích
          }
        }
      }
      return false // Objekt nebyl nalezen
    }
    const serialiedSlots = Object.keys(slots ?? {})
    for (let s = 0; s < serialiedSlots.length; s++) {
      if (deleteBlock(slots[serialiedSlots[s]!]!)) {
        return true
      }
    }
    return false
  })
}

export const duplicateBlock = (block: CONTENT_STRUCTURE_WITH_DATA): CONTENT_STRUCTURE_WITH_DATA => {
  const newBlockKey = crypto.randomUUID()

  const data = JSON.parse(JSON.stringify(block.data ?? {}))
  let contents = JSON.parse(JSON.stringify(block.contents ?? {}))
  const interactions = JSON.parse(JSON.stringify(block.interactions ?? {}))

  Object.entries(interactions).forEach(([key, value]) => {
    const newHash = crypto.randomUUID()
    delete interactions[key]
    interactions[newHash] = value
  })

  const updateContentHash = (contents: CONTENTS, keyToReplace: UUID, newKey: UUID) => {
    return Object.fromEntries(
      Object.entries(contents).map(([key, value]) => [
        key,
        Object.fromEntries(
          Object.entries(value).map(([blockKey, document]) => [
            blockKey === keyToReplace ? newKey : blockKey,
            document,
          ])
        ),
      ])
    )
  }

  if (data.properties?.additionalDocuments) {
    data.properties?.additionalDocuments?.forEach((path: string) => {
      const imageHash = get(data, `${path}.hash`)
      if (imageHash) {
        const newImageHash = crypto.randomUUID()
        contents = updateContentHash(contents, imageHash, newImageHash)
        set(data, `${path}.hash`, newImageHash)
      }
    })
  }

  const newBlock = {
    ...block,
    key: newBlockKey,
    params: JSON.parse(JSON.stringify(block.params ?? {})),
    data,
    interactions,
    contents: updateContentHash(contents, block.key!, newBlockKey),
    children: block.children?.map(duplicateBlock) || [],
  }
  return newBlock
}

export const duplicateBlockByKey = (
  slots: {
    [key: string]: CONTENT_STRUCTURE_SLOT
  },
  keyToDuplicate: string
): boolean => {
  const duplicate = (root: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT) => {
    if (!root || !root.children) return false

    for (let i = 0; i < root.children.length; i++) {
      if (root.children[i]!.key === keyToDuplicate) {
        const duplicatedBlock = duplicateBlock(root.children[i]!)
        root.children.splice(i + 1, 0, duplicatedBlock)
        return true
      }
      if (root.children[i]!.children && Array.isArray(root.children[i]!.children)) {
        const foundAndDuplicated = duplicate(root.children[i]!)
        if (foundAndDuplicated) {
          return true
        }
      }
    }
    return false
  }
  const serialiedSlots = Object.keys(slots ?? {})
  for (let s = 0; s < serialiedSlots.length; s++) {
    if (duplicate(slots[serialiedSlots[s]!]!)) {
      return true
    }
  }
  return false
}

export const appendBlock = (
  slots: {
    [key: string]: CONTENT_STRUCTURE_SLOT
  },
  keyToAppendBlock: string,
  block: CONTENT_STRUCTURE_WITH_DATA<ALL_BLOCKS_PARAMS, ALL_BLOCKS_DATA>,
  replace = false
): CONTENT_STRUCTURE_WITH_DATA<ALL_BLOCKS_PARAMS, ALL_BLOCKS_DATA> | null => {
  const append = (
    root: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT
  ): CONTENT_STRUCTURE_WITH_DATA | null => {
    if (!root || !root.children) return null

    for (let i = 0; i < root.children.length; i++) {
      if (root.children[i]!.key === keyToAppendBlock) {
        const duplicatedBlock = duplicateBlock(block)
        if (replace) {
          const newValue = {
            ...duplicatedBlock,
          }
          Object.keys(devices).forEach((deviceType) => {
            newValue.params[deviceType] = {
              ...newValue.params[deviceType],
              ...root.children[i]?.params[deviceType],
            }
          })
          root.children.splice(i, 1, JSON.parse(JSON.stringify(newValue)))
        } else {
          if (
            Array.isArray(root.children[i]?.children) &&
            (root.children[i]?.hasChildren || root.children[i]?.type === 'container')
          ) {
            root.children[i]?.children.splice(
              root.children[i]?.children?.length,
              0,
              duplicatedBlock
            )
          } else if (root.children[i]?.type === 'slot') {
            console.log(duplicatedBlock)
            root.children.splice(i + 1, 0, duplicatedBlock.children)
          } else {
            root.children.splice(i + 1, 0, duplicatedBlock)
          }
        }
        return duplicatedBlock
      }
      if (root.children[i]!.children && Array.isArray(root.children[i]!.children)) {
        const foundAndDuplicated = append(root.children[i]!)
        if (foundAndDuplicated) {
          return foundAndDuplicated
        }
      }
    }
    return null
  }
  const serialiedSlots = Object.keys(slots ?? {})
  for (let s = 0; s < serialiedSlots.length; s++) {
    if (slots[serialiedSlots[s]!].key === keyToAppendBlock) {
      const duplicatedBlock = duplicateBlock(block)
      const itemToReplace =
        duplicatedBlock.type === 'slot' ? duplicatedBlock.children : [duplicatedBlock]
      slots[serialiedSlots[s]!]?.children.push(...itemToReplace)
      return duplicatedBlock.type === 'slot' ? duplicatedBlock.children : duplicatedBlock
    }
    const appendedKey = append(slots[serialiedSlots[s]!]!)
    if (appendedKey) {
      return appendedKey
    }
  }
  return null
}

export const generateContainer = (
  defaultData?: CONTENT_STRUCTURE_WITH_DATA<CONTAINER_DATA>
): CONTENT_STRUCTURE_WITH_DATA<CONTAINER_PARAMS, CONTAINER_DATA> => {
  const uuid = crypto.randomUUID()
  const defaultContainerParams = DEFAULT_CONTAINER_PARAMS()
  return {
    type: 'container',
    key: uuid,
    params: {
      ...defaultContainerParams,
      ...defaultData?.params,
    },
    data: DEFAULT_CONTAINER_DATA(),
    children: [...(defaultData?.children ?? [])],
    contents: { document: {}, article: {}, branch: {}, product: {} },
  }
}

export const hasStructureThisKey = (
  root: CONTENT_STRUCTURE_ROOT | CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT,
  key: CONTENT_STRUCTURE_WITH_DATA<ALL_BLOCKS_PARAMS, ALL_BLOCKS_DATA>['key']
): boolean => {
  if ('key' in root && root.key === key) {
    return true
  }
  if (Array.isArray(root.children)) {
    return root.children.some((child) => hasStructureThisKey(child, key))
  } else if (root.children && typeof root.children === 'object') {
    return Object.values(root.children).some((child) => hasStructureThisKey(child, key))
  }
  return false
}

export const selectBlock = (
  slots: {
    [key: UUID]: CONTENT_STRUCTURE_SLOT
  },
  key: CONTENT_STRUCTURE_WITH_DATA<ALL_BLOCKS_PARAMS, ALL_BLOCKS_DATA>['key']
): { block: CONTENT_STRUCTURE_WITH_DATA; child?: CONTENT_STRUCTURE_WITH_DATA } | null => {
  // Helper function to recursively search for the block and track parent
  const searchBlock = (
    block: CONTENT_STRUCTURE_WITH_DATA,
    parent: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT | null
  ): { block: CONTENT_STRUCTURE_WITH_DATA; child?: CONTENT_STRUCTURE_WITH_DATA } | null => {
    if (block.key === key) {
      const BlocksInstance = Blocks.getInstance()
      const BLOCK_COMPONENTS = BlocksInstance.getComponents()
      return BLOCK_COMPONENTS[block.data.type]?.edit
        ? { block }
        : { block: parent as CONTENT_STRUCTURE_WITH_DATA, child: block }
    }

    if (block.children && Array.isArray(block.children)) {
      for (const child of block.children) {
        const result = searchBlock(child, block)
        if (result) return { ...result } // Return immediately if found
      }
    }

    return null
  }
  const serializedSlots = Object.values(slots)
  for (const slot of serializedSlots) {
    for (const child of slot.children) {
      const block = searchBlock(child, slot)
      if (block) {
        return block
      }
    }
  }

  return null // Return null if no matching block is found
}

export const findBlockInContents = (allContents, key) => {
  // Rekurzivní funkce pro hledání prvku podle klíče
  const findElementByKey = (tree, key) => {
    if (!tree || typeof tree !== 'object') return null

    // Pokud aktuální uzel obsahuje hledaný klíč, vrátíme ho
    if (tree.key === key) {
      return tree
    }

    // Pokud má uzel children, rekurzivně prohledáme každý z nich
    if (Array.isArray(tree.children)) {
      for (const child of tree.children) {
        const result = findElementByKey(child, key)
        if (result) return result // Pokud jsme našli výsledek, vracíme ho
      }
    }

    return null // Pokud nic nenalezneme, vrátíme null
  }

  // Prohledáme všechny contents a hledáme odpovídající prvek
  for (const { content } of Object.values(allContents)) {
    // Pro každý blok ve children
    for (const s of Object.values(content.structure.children)) {
      const result = findElementByKey(s, key)
      if (result) {
        return result // Pokud jsme našli, vrátíme výsledek a ukončíme hledání
      }
    }
  }

  // Pokud nic nenalezneme, vrátíme null
  return null
}

export const updateHTMLFontSize = (clientWidth: number) => {
  const ContentInstance = Content.getInstance()

  const entries = Object.entries(ContentInstance.getFontSizes())
    .map(([minWidthStr, size]) => ({
      minWidth: parseInt(minWidthStr, 10),
      ...size,
    }))
    .filter((entry) => !isNaN(entry.minWidth) && clientWidth >= entry.minWidth)
    .sort((a, b) => b.minWidth - a.minWidth) // Seřadí od největší po nejmenší

  const selected = entries[0]
  if (selected) {
    const { value, unit } = selected
    document.documentElement.style.fontSize = `${value}${unit}`
  }
}

export const collectKeys = (
  node: any,
  isFirst = true,
  map: { [key: UUID]: true } = {},
  filter: (node: any) => boolean = () => true
) => {
  if (!(node || !node.key)) return map

  if (filter(node) && node.key) {
    map[node.key] = true
  }

  if (isFirst && node.children && typeof node.children === 'object') {
    // children je objekt (např. { default: Node })
    Object.values(node.children).forEach((child) => collectKeys(child, false, map))
  } else if (Array.isArray(node.children)) {
    // children je pole (např. [Node, Node, ...])
    node.children.forEach((child) => collectKeys(child, false, map))
  }

  return map
}

export const checkAllNodesHaveDataType = (node: any, isFirst = true): boolean => {
  if (!node) return true
  // Kontrola aktuálního node
  if (!isFirst && (!node.data || !node.data.type)) {
    return false
  }

  // Rekurzivní kontrola children
  if (isFirst && node.children && typeof node.children === 'object') {
    // children je objekt (např. { default: Node })
    return Object.values(node.children).every((child) => checkAllNodesHaveDataType(child, false))
  } else if (Array.isArray(node.children)) {
    // children je pole (např. [Node, Node, ...])
    return node.children.every((child) => checkAllNodesHaveDataType(child, false))
  }

  return true
}
